"use client"

import {
  captureAnalyticsEvent,
  captureNewsletterSubscribed,
} from "@/lib/analytics/posthog-client"
import { applyPromotions } from "@/lib/data/cart"
import { subscribeToNewsletter } from "@/lib/data/newsletter"
import * as Dialog from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import { FormEvent, useEffect, useRef, useState } from "react"

const STORAGE_KEY = "newsletter_popup_state"
const SHOW_DELAY_MS = 10_000
const NEWSLETTER_CODE = "NEWSLETTER5"

type StoredState = "subscribed" | "dismissed" | null

const readState = (): StoredState => {
  if (typeof window === "undefined") return null
  try {
    const v = window.localStorage.getItem(STORAGE_KEY)
    if (v === "subscribed" || v === "dismissed") return v
    return null
  } catch {
    return null
  }
}

const writeState = (s: Exclude<StoredState, null>) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, s)
  } catch {
    /* swallow */
  }
}

type Status = "idle" | "loading" | "success" | "error"

/** Why the modal opened (for funnel: shown → dismiss / subscribe). */
type PopupShowTrigger = "timer" | "exit_intent"

/** How the user closed without subscribing (Radix overlay / escape vs explicit UI). */
type PopupDismissAction = "close_x" | "decline_text" | "overlay" | "escape"

const NewsletterPopup = () => {
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState("")
  const [status, setStatus] = useState<Status>("idle")
  const [errorMessage, setErrorMessage] = useState("")
  const triggeredRef = useRef(false)
  /** Ensures at most one dismiss analytics + storage write per open cycle (Radix can fire multiple close paths). */
  const dismissHandledRef = useRef(false)
  /** At most one analytics event when closing the thank-you step (overlay / Esc / button can overlap). */
  const postSubscribeCloseTrackedRef = useRef(false)

  // First-visit + exit-intent trigger
  useEffect(() => {
    if (readState() !== null) return

    const reveal = (trigger: PopupShowTrigger) => {
      if (triggeredRef.current) return
      triggeredRef.current = true
      dismissHandledRef.current = false
      postSubscribeCloseTrackedRef.current = false
      setOpen(true)
      captureAnalyticsEvent("newsletter_popup_shown", {
        trigger,
      })
    }

    const timer = window.setTimeout(() => reveal("timer"), SHOW_DELAY_MS)

    const onMouseLeave = (e: MouseEvent) => {
      // Desktop exit-intent: cursor moves above the viewport.
      if (e.clientY <= 0) reveal("exit_intent")
    }
    document.documentElement.addEventListener("mouseleave", onMouseLeave)

    return () => {
      window.clearTimeout(timer)
      document.documentElement.removeEventListener("mouseleave", onMouseLeave)
    }
  }, [])

  const handleDismiss = (dismissAction: PopupDismissAction) => {
    if (dismissHandledRef.current) return
    dismissHandledRef.current = true
    writeState("dismissed")
    setOpen(false)
    captureAnalyticsEvent("newsletter_popup_dismissed", {
      dismiss_action: dismissAction,
      had_email_input: email.trim().length > 0,
      form_status: status,
    })
  }

  /** After a successful subscribe — close only; do not mark dismissed or double-count funnel exit. */
  const handleCloseAfterSuccess = (
    closeMethod: "continue_button" | "overlay" | "escape" | "close_x" = "continue_button"
  ) => {
    if (!postSubscribeCloseTrackedRef.current) {
      postSubscribeCloseTrackedRef.current = true
      captureAnalyticsEvent("newsletter_popup_closed_after_subscribe", {
        close_method: closeMethod,
      })
    }
    setOpen(false)
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    setStatus("loading")
    setErrorMessage("")

    const result = await subscribeToNewsletter(email, "popup")

    if (!result.ok) {
      setStatus("error")
      setErrorMessage(result.message)
      captureAnalyticsEvent("form_error_triggered", {
        field_name: "newsletter_email",
        form_name: "newsletter_popup",
      })
      return
    }

    // Best-effort: if there's already a cart, push the code in optimistically.
    // The backend auto-applies on cart events too, but this shortens the wait.
    try {
      await applyPromotions([NEWSLETTER_CODE])
    } catch {
      /* swallow — eligibility may not be ready until the cart has the email */
    }

    setStatus("success")
    writeState("subscribed")
    postSubscribeCloseTrackedRef.current = false
    captureNewsletterSubscribed("popup", "newsletter_popup")
  }

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-navy/80 backdrop-blur-sm animate-[newsletter-fade_280ms_ease-out]" />
        <div className="fixed inset-0 z-50 overflow-y-auto overscroll-y-contain pointer-events-none pt-[max(0.75rem,env(safe-area-inset-top,0px))] pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] pl-[max(0.75rem,env(safe-area-inset-left,0px))] pr-[max(0.75rem,env(safe-area-inset-right,0px))]">
          <div className="flex min-h-full items-center justify-center p-3 sm:p-5">
            <Dialog.Content
              className="pointer-events-auto relative mx-auto w-full max-w-[560px] max-h-[min(85dvh,720px)] overflow-y-auto overscroll-contain rounded-2xl border border-gold/50 bg-navy text-cream p-5 sm:p-8 md:p-10 shadow-2xl shadow-black/40 animate-[newsletter-rise_320ms_ease-out_forwards]"
              aria-describedby={undefined}
              onEscapeKeyDown={(e) => {
                e.preventDefault()
                if (status === "success") {
                  handleCloseAfterSuccess("escape")
                  return
                }
                handleDismiss("escape")
              }}
              onPointerDownOutside={(e) => {
                e.preventDefault()
                if (status === "success") {
                  handleCloseAfterSuccess("overlay")
                  return
                }
                handleDismiss("overlay")
              }}
            >
            <button
              type="button"
              onClick={() =>
                status === "success"
                  ? handleCloseAfterSuccess("close_x")
                  : handleDismiss("close_x")
              }
              className="absolute right-2 top-2 sm:right-4 sm:top-4 flex h-11 w-11 items-center justify-center rounded-full text-cream/70 hover:text-gold hover:bg-gold/10 transition touch-manipulation"
              aria-label="Zapri"
            >
              <X size={18} aria-hidden />
            </button>

            {status !== "success" ? (
              <>
                <p className="text-center text-[11px] tracking-[0.32em] text-gold uppercase">
                  Krek za vas
                </p>

                <Dialog.Title className="mt-3 pr-10 text-center text-[1.35rem] leading-snug sm:text-2xl md:text-3xl font-serif text-cream sm:mt-4 sm:pr-0">
                  Spoštovani gost,
                </Dialog.Title>

                <div className="mt-4 flex items-center justify-center sm:mt-6">
                  <span
                    className="text-5xl leading-none sm:text-6xl md:text-7xl font-serif text-gold animate-[newsletter-percent_700ms_ease-out]"
                    aria-label="Minus 5 odstotkov"
                  >
                    −5&nbsp;%
                  </span>
                </div>

                <p className="mt-3 text-center text-[11px] leading-relaxed sm:mt-4 sm:text-xs md:text-sm tracking-wide sm:tracking-[0.2em] text-cream/90 uppercase px-1">
                  Ob prvem naročilu · Krek
                </p>

                <form
                  onSubmit={handleSubmit}
                  className="mt-6 flex flex-col gap-3 sm:mt-7 sm:flex-row"
                >
                  <label htmlFor="newsletter-popup-email" className="sr-only">
                    E-poštni naslov
                  </label>
                  <input
                    id="newsletter-popup-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="vas@email.si"
                    className="min-h-12 w-full flex-1 rounded-full bg-cream text-base text-navy px-5 py-3.5 placeholder:text-navy/45 focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2 focus:ring-offset-navy sm:min-h-0 sm:py-3"
                    autoComplete="email"
                    inputMode="email"
                    enterKeyHint="send"
                    disabled={status === "loading"}
                  />
                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="min-h-12 w-full shrink-0 rounded-full bg-gold px-6 py-3.5 text-sm tracking-[0.12em] uppercase text-navy font-semibold hover:bg-cream transition touch-manipulation disabled:opacity-70 sm:min-h-0 sm:w-auto sm:py-3 sm:tracking-[0.14em]"
                  >
                    {status === "loading" ? "Pošiljam …" : "Uveljavi ponudbo"}
                  </button>
                </form>

                {status === "error" ? (
                  <p className="mt-3 text-center text-sm text-terracotta">
                    {errorMessage}
                  </p>
                ) : (
                  <p className="mt-3 text-center text-xs text-cream/65">
                    Brez neželene pošte. Odjava kadarkoli.
                  </p>
                )}

                <button
                  type="button"
                  onClick={() => handleDismiss("decline_text")}
                  className="mt-4 sm:mt-5 mx-auto block min-h-11 px-2 text-sm text-cream/75 hover:text-gold underline underline-offset-4 touch-manipulation"
                >
                  Ne, hvala
                </button>
              </>
            ) : (
              <div className="flex flex-col items-center text-center py-2 sm:py-4 pr-2">
                <p className="text-[11px] tracking-[0.32em] text-gold uppercase">
                  Hvala
                </p>
                <Dialog.Title className="mt-3 text-xl leading-snug sm:mt-4 sm:text-2xl md:text-3xl font-serif text-cream">
                  Vaš popust je pripravljen
                </Dialog.Title>
                <div className="mt-5 inline-flex max-w-full items-center gap-2 overflow-x-auto rounded-full border border-gold/50 bg-gold/10 px-4 py-2.5 font-mono text-base tracking-[0.2em] text-gold sm:mt-6 sm:px-5 sm:text-lg sm:tracking-[0.32em]">
                  {NEWSLETTER_CODE}
                </div>
                <p className="mt-4 text-sm text-cream/80 max-w-sm px-1 sm:mt-5">
                  −5 % popust je vezan na vaš e-naslov in se samodejno uporabi
                  ob prvi košarici.
                </p>
                <p className="mt-2 text-xs text-cream/55 max-w-sm px-1">
                  Velja samo za prvo naročilo na tem e-naslovu.
                </p>
                <button
                  type="button"
                  onClick={() => handleCloseAfterSuccess("continue_button")}
                  className="mt-6 min-h-12 w-full max-w-xs rounded-full bg-gold px-6 py-3.5 text-sm tracking-[0.12em] uppercase font-semibold text-navy hover:bg-cream transition touch-manipulation sm:mt-7 sm:w-auto sm:py-3 sm:tracking-[0.14em]"
                >
                  Nadaljuj z nakupovanjem
                </button>
              </div>
            )}
            </Dialog.Content>
          </div>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export default NewsletterPopup
